import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Check, FileText, ExternalLink, Trash2 } from 'lucide-react';
import testPackageService from '../../../services/testPackageService';
import { useCart } from '../../../context/CartContext';
import Header from '../../common/Header';
import Footer from '../../common/Footer';

const API_BASE_URL = 'https://www.homecureexpress.com';

const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  return `${API_BASE_URL}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
};

export default function TestPackageDetail() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { cart, addMedicalPackage, isAuthenticated } = useCart();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [relatedPackages, setRelatedPackages] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  useEffect(() => {
    fetchPackageDetails();
    fetchRelatedPackages();
  }, [packageId]);

  const fetchPackageDetails = async () => {
    try {
      setLoading(true);
      const response = await testPackageService.getTestPackageById(packageId);
      if (response.success) {
        setPackageData(response.data);
      }
    } catch (err) {
      setError(err.error || 'Failed to load package details');
      console.error('Error fetching package details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPackages = async () => {
    try {
      setLoadingRelated(true);
      const response = await testPackageService.getRelatedPackages(packageId);
      if (response.success && response.related_packages) {
        setRelatedPackages(response.related_packages);
      }
    } catch (err) {
      console.error('Error fetching related packages:', err);
      setRelatedPackages([]);
    } finally {
      setLoadingRelated(false);
    }
  };

  // Check if package is in cart (from backend)
  const getPackageQuantityInCart = () => {
    if (!cart || !cart.test_items) return 0;
    const cartItem = cart.test_items.find(item => item.package?.id === parseInt(packageId));
    return cartItem ? cartItem.quantity : 0;
  };

  const quantityInCart = getPackageQuantityInCart();

  const handleAddToCart = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    if (!packageData) return;

    setIsAddingToCart(true);
    try {
      await addMedicalPackage(parseInt(packageId), 1);
      alert('Package added to cart successfully!');
    } catch (error) {
      console.error('Error adding package to cart:', error);
      alert(error.message || 'Failed to add package to cart');
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleToggleCart = () => {
    if (quantityInCart > 0) {
      navigate('/medical-tests/booking');
    } else {
      handleAddToCart();
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #104a5f',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
            <p style={{ marginTop: '16px', color: '#666' }}>Loading package details...</p>
          </div>
        </div>
        <Footer />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  if (error || !packageData) {
    return (
      <>
        <Header />
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ textAlign: 'center', maxWidth: '500px' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
            <h2 style={{ color: '#dc2626', marginBottom: '12px', fontSize: '24px' }}>Package Not Found</h2>
            <p style={{ color: '#666', marginBottom: '24px' }}>{error || 'The package you are looking for does not exist.'}</p>
            <button
              onClick={() => navigate('/medical-tests')}
              style={{
                background: '#104a5f',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              Browse All Packages
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', paddingBottom: '40px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 16px' }}>
          {/* Back Button */}
          <button
            onClick={() => navigate('/medical-tests')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'transparent',
              border: 'none',
              color: '#104a5f',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '20px',
              padding: '8px 0'
            }}
          >
            <ArrowLeft size={20} />
            Back to Packages
          </button>

          {/* Main Content */}
          <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            {/* Package Header */}
            <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#104a5f', marginBottom: '12px' }}>
                    {packageData.name}
                  </h1>

                  {packageData.discount_percentage > 0 && (
                    <span style={{
                      display: 'inline-block',
                      background: '#10b981',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '700',
                      marginBottom: '16px'
                    }}>
                      {Math.round(packageData.discount_percentage)}% OFF
                    </span>
                  )}

                  <p style={{ color: '#666', fontSize: '16px', lineHeight: '1.6', marginTop: '12px' }}>
                    {packageData.description}
                  </p>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
                    <div style={{
                      background: '#f0f9ff',
                      color: '#104a5f',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <FileText size={16} />
                      {packageData.tests_count || packageData.test_count} Tests Included
                    </div>
                  </div>
                </div>
              </div>

              {/* Price and Add to Cart */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '16px',
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#104a5f' }}>
                    ₹{packageData.offer_price || packageData.effective_price}
                  </div>
                  {packageData.total_price && packageData.total_price !== packageData.offer_price && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                      <span style={{ fontSize: '18px', color: '#999', textDecoration: 'line-through' }}>
                        ₹{packageData.total_price}
                      </span>
                      <span style={{ fontSize: '14px', color: '#10b981', fontWeight: '600' }}>
                        Save ₹{(parseFloat(packageData.total_price) - parseFloat(packageData.offer_price)).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    onClick={handleToggleCart}
                    disabled={isAddingToCart}
                    style={{
                      background: quantityInCart > 0 ? '#10b981' : '#104a5f',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '14px 28px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: isAddingToCart ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.3s ease',
                      opacity: isAddingToCart ? 0.7 : 1
                    }}
                  >
                    {isAddingToCart ? (
                      <>
                        <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                        Adding...
                      </>
                    ) : quantityInCart > 0 ? (
                      <>
                        <Check size={20} />
                        {quantityInCart} in Cart - View Cart
                      </>
                    ) : (
                      <>
                        <ShoppingCart size={20} />
                        Add to Cart
                      </>
                    )}
                  </button>

                  {quantityInCart > 0 && (
                    <button
                      onClick={() => navigate('/medical-tests/booking')}
                      style={{
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '14px 28px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <ExternalLink size={20} />
                      Go to Cart
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Included Tests */}
            {packageData.included_tests && packageData.included_tests.length > 0 && (
              <div style={{ padding: '24px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#104a5f', marginBottom: '20px' }}>
                  Tests Included ({packageData.included_tests.length})
                </h2>

                <div style={{ display: 'grid', gap: '12px' }}>
                  {packageData.included_tests.map((test, index) => (
                    <div
                      key={test.id}
                      style={{
                        padding: '16px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        borderLeft: '4px solid #104a5f'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#333', marginBottom: '6px' }}>
                            {index + 1}. {test.title}
                          </h3>
                          <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                            {test.description}
                          </p>
                        </div>
                        {test.offer_price && (
                          <div style={{ textAlign: 'right', minWidth: '80px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#104a5f' }}>
                              ₹{test.offer_price}
                            </div>
                            {test.price && test.price !== test.offer_price && (
                              <div style={{ fontSize: '12px', color: '#999', textDecoration: 'line-through' }}>
                                ₹{test.price}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Related Packages */}
          {relatedPackages.length > 0 && (
            <div style={{ marginTop: '40px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#104a5f', marginBottom: '20px' }}>
                Similar Packages You Might Like
              </h2>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '20px'
              }}>
                {loadingRelated ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}>
                      <div style={{ height: '200px', background: '#f0f0f0', borderRadius: '8px' }}></div>
                    </div>
                  ))
                ) : (
                  relatedPackages.map((pkg) => (
                    <div
                      key={pkg.id}
                      onClick={() => navigate(`/test-package/${pkg.id}`)}
                      style={{
                        background: 'white',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                      }}
                    >
                      {pkg.image && (
                        <img
                          src={getImageUrl(pkg.image)}
                          alt={pkg.name}
                          style={{
                            width: '100%',
                            height: '140px',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            if (!e.target.dataset.errored) {
                              e.target.dataset.errored = 'true';
                              e.target.style.display = 'none';
                            }
                          }}
                        />
                      )}

                      <div style={{ padding: '16px' }}>
                        {pkg.discount_percentage > 0 && (
                          <span style={{
                            display: 'inline-block',
                            background: '#10b981',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '700',
                            marginBottom: '8px'
                          }}>
                            {Math.round(pkg.discount_percentage)}% OFF
                          </span>
                        )}

                        <h3 style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#333',
                          marginBottom: '8px',
                          lineHeight: '1.3'
                        }}>
                          {pkg.name}
                        </h3>

                        <p style={{
                          fontSize: '13px',
                          color: '#666',
                          marginBottom: '12px',
                          lineHeight: '1.4',
                          height: '40px',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {pkg.description}
                        </p>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          marginBottom: '12px',
                          fontSize: '13px',
                          color: '#104a5f',
                          fontWeight: '600'
                        }}>
                          <FileText size={14} />
                          <span>{pkg.test_count} Tests</span>
                        </div>

                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingTop: '12px',
                          borderTop: '1px solid #e5e7eb'
                        }}>
                          <div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#104a5f' }}>
                              ₹{pkg.offer_price}
                            </div>
                            {pkg.total_price && pkg.total_price !== pkg.offer_price && (
                              <div style={{ fontSize: '12px', color: '#999', textDecoration: 'line-through' }}>
                                ₹{pkg.total_price}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/test-package/${pkg.id}`);
                            }}
                            style={{
                              background: '#104a5f',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
